/**
 * Custom block template renderers.
 *
 * These renderers handle blocks created via the no-code builder.
 * Each template type (card-grid, info-panel, calculator, form) has
 * a corresponding renderer that interprets the custom configuration.
 *
 * Security: No user code is executed. All rendering uses our
 * secure built-in rendering with escaped values.
 */

import { registerBlockRenderer } from '../registry';

// Types
export type { CustomBlockConfig, CardFieldConfig, InfoPanelSection, CalculatorInput, FormField, TemplateRenderer } from './types';
import type { CustomBlockConfig, TemplateRenderer } from './types';

// Template renderers
import { renderCustomCardGrid } from './card-grid';
import { renderCustomInfoPanel } from './info-panel';
import { renderCustomCalculator } from './calculator';
import { renderCustomForm } from './form';
import { renderCustomStats } from './stats';
import { renderCustomComparison } from './comparison';
import { renderCustomTimeline } from './timeline';
import { renderCustomTestimonial } from './testimonial';
import { renderCustomAlert } from './alert';

// =============================================================================
// TEMPLATE REGISTRY
// =============================================================================

const templateRenderers: Record<string, TemplateRenderer> = {
  'card-grid': renderCustomCardGrid,
  'info-panel': renderCustomInfoPanel,
  'calculator': renderCustomCalculator,
  'form': renderCustomForm,
  'stats': renderCustomStats,
  'comparison': renderCustomComparison,
  'timeline': renderCustomTimeline,
  'testimonial': renderCustomTestimonial,
  'alert': renderCustomAlert,
};

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Register a custom block with the block registry.
 * Creates a renderer that uses the appropriate template.
 */
export function registerCustomBlock(block: CustomBlockConfig): void {
  const templateRenderer = templateRenderers[block.template];
  if (!templateRenderer) {
    console.warn(`[CustomBlocks] Unknown template type: ${block.template}`);
    return;
  }

  // Register the block renderer with the dynamic configuration
  registerBlockRenderer(block.blockType, (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
    const blockData = data as Record<string, unknown>;
    templateRenderer(block.config, blockData, container, onAction, block.themeOverrides);
  });

  console.debug(`[CustomBlocks] Registered custom block: ${block.blockType} (${block.template})`);
}

/**
 * Register multiple custom blocks.
 */
export function registerCustomBlocks(blocks: CustomBlockConfig[]): void {
  for (const block of blocks) {
    registerCustomBlock(block);
  }
  console.debug(`[CustomBlocks] Registered ${blocks.length} custom blocks`);
}
